/**
 * Checks if the user has set a preference for reduced motion.
 * Returns true if the user prefers reduced motion, false otherwise.
 *
 * This function defensively checks for the presence of the window object and matchMedia.
 *
 * @returns {boolean} True if the user prefers reduced motion; otherwise, false.
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    // On the server or if matchMedia is not available, assume no reduced motion preference.
    return false;
  }
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Scrolls to the top of the page while respecting the user's reduced motion preference.
 *
 * When invoked, this function scrolls the window to the top. If the user has explicitly set a
 * reduced motion preference, the scroll behavior will be set to 'auto' (instant). Otherwise,
 * the scrolling will be smooth. This function also checks defensively to ensure it's running
 * in a browser environment.
 *
 * @async
 * @returns {Promise<void>} A promise that resolves once the scroll command has been issued.
 */
export async function scrollToTopRespectingMotion(): Promise<void> {
  if (typeof window === "undefined") {
    return;
  }

  window.scrollTo({
    top: 0,
    behavior: prefersReducedMotion() ? "auto" : "smooth",
  });
}

export const scrollToFirstInvalidField = () => {
  const inputFieldError = document.querySelector("[data-test-id$='-message']");
  if (inputFieldError) {
    const parent = inputFieldError.parentElement;
    if (parent) parent.scrollIntoView({ behavior: "smooth" });
  }
};
